import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Alert } from '@mui/material';
import { Route as RouteIcon, DirectionsRun as RunIcon, Speed as PaceIcon, Timer as TimerIcon, Insights as ActivityIcon, EmojiEvents as TrophyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../i18n/locale-format';
import { Contest, WorkoutActivity } from '../models/types';
import { apiService } from '../services/api.service';
import { userService } from '../services/user.service';
import { displayNameOf } from '../components/layout/useAppShellData';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile, StatGrid } from '../components/ui/StatTile';
import { SurfaceCard } from '../components/ui/SurfaceCard';
import { EmptyState } from '../components/ui/EmptyState';
import { activityKindOf, formatDuration, formatPace } from '../components/activities/activityHelpers';
import { greetingFor, summarizeWeek } from './home/homeStats';
import { WeekChartCard } from './home/WeekChartCard';
import { ActiveChallengeCard } from './home/ActiveChallengeCard';
import { RecentActivitiesCard } from './home/RecentActivitiesCard';
import { UpcomingChallengesCard } from './home/UpcomingChallengesCard';

const twoColumnGridSx = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 2, alignItems: 'start' } as const;

export const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('home');
  const [contests, setContests] = useState<Contest[]>([]);
  const [activities, setActivities] = useState<WorkoutActivity[]>([]);
  const [firstName, setFirstName] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [contestsRes, activitiesRes, profileRes] = await Promise.allSettled([
          apiService.getContests(),
          apiService.getUserActivities(),
          userService.getCurrentUserProfile(),
        ]);
        // Contests are the page's core data; activities and profile degrade gracefully.
        if (contestsRes.status === 'rejected') throw contestsRes.reason;
        setContests(contestsRes.value);
        if (activitiesRes.status === 'fulfilled') setActivities(activitiesRes.value);
        else console.error('Error fetching activities:', activitiesRes.reason);
        if (profileRes.status === 'fulfilled') setFirstName(displayNameOf(profileRes.value.data).split(' ')[0] || '');
        setLoadFailed(false);
      } catch (err) {
        setLoadFailed(true);
        console.error('Error fetching contests:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const { active, upcoming } = useMemo(() => {
    const now = Date.now();
    const activeList = contests
      .filter(c => new Date(c.startAt).getTime() <= now && new Date(c.endAt).getTime() >= now)
      .sort((a, b) => new Date(a.endAt).getTime() - new Date(b.endAt).getTime());
    const upcomingList = contests
      .filter(c => new Date(c.startAt).getTime() > now)
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
    return { active: activeList, upcoming: upcomingList };
  }, [contests]);

  const week = useMemo(() => summarizeWeek(activities), [activities]);
  const recent = useMemo(
    () => [...activities].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()).slice(0, 4),
    [activities]
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadFailed) return <Alert severity="error">{t('loadFailed')}</Alert>;

  const now = new Date();
  const distanceDelta = week.distance - week.previousDistance;
  const runCount = week.current.filter(a => activityKindOf(a.workoutType) === 'Run').length;
  const paceDeltaSec = week.runPace > 0 && week.previousRunPace > 0 ? Math.round((week.runPace - week.previousRunPace) * 60) : null;

  const distanceNote = week.previousDistance > 0
    ? t('stats.distanceVsPrevious', { sign: distanceDelta >= 0 ? '+' : '−', value: Math.abs(distanceDelta).toFixed(1) })
    : undefined;
  const longestNote = week.longest
    ? t('stats.longest', { distance: week.longest.distance.toFixed(1), weekday: formatDate(week.longest.startDate, { weekday: 'long' }) })
    : undefined;
  const paceNote = paceDeltaSec === null || paceDeltaSec === 0
    ? undefined
    : t(paceDeltaSec < 0 ? 'stats.paceFaster' : 'stats.paceSlower', { sec: Math.abs(paceDeltaSec) });
  const greeting = greetingFor(now);

  return (
    <>
      <PageHeader
        eyebrow={formatDate(now, { weekday: 'long', month: 'long', day: 'numeric' })}
        title={firstName ? t('greeting.withName', { greeting, name: firstName }) : greeting}
        actions={<Button variant="outlined" startIcon={<ActivityIcon />} onClick={() => navigate('/activities')}>{t('header.viewActivities')}</Button>}
      />

      <StatGrid>
        <StatTile label={t('stats.distance')} icon={<RouteIcon />} value={week.distance.toFixed(1)} unit="km" note={distanceNote} />
        <StatTile label={runCount > 0 ? t('stats.runs') : t('stats.activities')} icon={<RunIcon />} value={runCount > 0 ? runCount : week.current.length} note={longestNote} />
        <StatTile label={t('stats.avgRunPace')} icon={<PaceIcon />} value={week.runPace > 0 ? formatPace(week.runPace) : '–'} unit={week.runPace > 0 ? '/km' : undefined} note={paceNote} />
        <StatTile label={t('stats.movingTime')} icon={<TimerIcon />} value={formatDuration(week.time)} note={t('stats.across', { count: week.current.length })} />
      </StatGrid>

      <Box sx={twoColumnGridSx}>
        <WeekChartCard week={week} />
        {active.length > 0 ? (
          <ActiveChallengeCard contest={active[0]!} extraCount={active.length - 1} onOpen={() => navigate(`/contests/${active[0]!._id}`)} />
        ) : (
          <SurfaceCard>
            <EmptyState
              icon={<TrophyIcon />}
              title={t('noActiveChallenge.title')}
              description={t('noActiveChallenge.description')}
              action={<Button variant="contained" onClick={() => navigate('/groups')}>{t('noActiveChallenge.action')}</Button>}
            />
          </SurfaceCard>
        )}
      </Box>

      <Box sx={twoColumnGridSx}>
        <RecentActivitiesCard activities={recent} />
        <UpcomingChallengesCard contests={upcoming.slice(0, 3)} />
      </Box>
    </>
  );
};
